<template lang="html">
  <section :style="themableStyle" class="theme-color">
    <div class="card">
      <div class="card-content">
        <article class="media">
          <!-- Comment Avatar -->
          <figure class="media-left">
            <p class="image is-48x48">
              <img
                style="border-radius: 50%"
                :src="comment.attributes.avatar.url"
                alt="Placeholder image"
              />
            </p>
          </figure>
          <div class="media-content">
            <div class="content">
              <p>
                <!-- Comment Header -->
                <router-link :to="{ path: `/users/${comment.attributes.username}` }">
                  <!-- Cosplayer Name -->
                  <span class="is-size-5 has-text-weight-semibold">
                    {{ comment.attributes.cosplayer_name }}
                  </span>
                  <!-- Teacher icon -->
                  <b-tooltip append-to-body label="Teacher" animated>
                    <b-icon v-if="teacher" size="is-small" icon="school"></b-icon>
                  </b-tooltip>
                  <!-- Username -->
                  <small class="has-text-gray">
                    @{{ comment.attributes.username }} -
                  </small>
                </router-link>
                <!-- Creation date -->

                <small class="has-text-gray pr-1">
                  <b-tooltip
                    append-to-body
                    :label="dateFormat(comment.attributes.created_at)"
                    animated
                  >
                    <time datetime="2018-07-07">
                      {{ dateFromNow(comment.attributes.created_at) }}
                    </time>
                  </b-tooltip>
                </small>

                <!-- Edition date -->
                <small class="has-text-gray is-size-7" v-if="comment.attributes.edited">
                  <b-tooltip
                    append-to-body
                    :label="dateFromNow(comment.attributes.updated_at)"
                    animated
                  >
                    (Edited)
                  </b-tooltip>
                </small>
                <br />
                <!-- Comment Content -->
                <span v-if="!editing">
                  {{ comment.attributes.content }}
                </span>
                <span v-else>
                  <b-input
                    maxlength="100"
                    ref="comment"
                    class="comment"
                    rows="1"
                    @keydown.native.enter.exact.prevent
                    @keyup.native.escape.exact="cancelEditing"
                    @keyup.native.enter.exact="saveEditing"
                    placeholder="Your comment ..."
                    v-model="content"
                    type="textarea"
                  ></b-input>
                </span>
              </p>
            </div>
            <nav class="level is-mobile">
              <div class="level-left">
                <!-- Comment Reply -->
                <a
                  @click="startReplying('')"
                  v-if="isLoggedIn && !replying && !editing"
                  class="level-item"
                >
                  Reply
                </a>
                <span class="level-item" v-if="editable && !replying && !editing">
                  -
                </span>
                <!-- Comment Edit -->
                <a
                  @click="startEditing"
                  v-if="editable && !replying && !editing"
                  class="level-item"
                >
                  Edit
                </a>

                <p class="buttons">
                  <b-button @click="cancelEditing" v-if="editable && editing">
                    Cancel
                  </b-button>
                  <b-button
                    icon-left="check"
                    @click="saveEditing"
                    v-if="editable && editing"
                    type="is-success"
                  >
                    Save
                  </b-button>
                </p>
              </div>
            </nav>
            <div>
              <comment-reply
                v-for="reply in replies"
                :key="reply.id"
                :reply="reply"
                :commentableOwner="commentableOwner"
                :teacher="reply.attributes.user_id === teacherId"
                @startReplying="startReplying"
                @deleteReply="deleteReply"
                @updateReply="updateReply"
                :theme="theme"
              />
            </div>
          </div>

          <div class="media-right">
            <!-- Comment Options -->
            <b-dropdown
              v-if="!editing"
              append-to-body
              aria-role="list"
              position="is-bottom-left"
            >
              <p class="clickable" slot="trigger" role="button">
                <b-icon size="is-small" type="is-danger" icon="block-helper"></b-icon>
              </p>

              <b-dropdown-item
                v-if="deletable"
                aria-role="listitem"
                @click="confirmDeletion"
                ><b-icon size="is-small" type="is-danger" icon="delete"></b-icon> Delete
                comment</b-dropdown-item
              >
              <b-dropdown-item v-if="reportable" aria-role="listitem"
                ><b-icon size="is-small" type="is-danger" icon="account-cancel"></b-icon>
                Block user</b-dropdown-item
              >
              <b-dropdown-item v-if="reportable" aria-role="listitem"
                ><b-icon size="is-small" type="is-danger" icon="alert-octagon"></b-icon>
                Report user</b-dropdown-item
              >
            </b-dropdown>
          </div>
        </article>

        <create-comment
          v-if="isLoggedIn && replying"
          @addComment="addReply"
          @cancelReply="cancelReply"
          :replying="replying"
          :replyContent="replyContent"
          :theme="theme"
        />
      </div>
    </div>
  </section>
</template>

<script>
import CreateComment from './CreateComment'
import CommentReply from './CommentReply'
import { mapGetters } from 'vuex'
import * as moment from 'moment'
import { dateMixin } from '@/mixins/dateMixin'
import { themableMixin } from '@/mixins/themableMixin'

export default {
  props: ['comment', 'commentableOwner', 'teacher', 'teacherId', 'theme'],
  mixins: [dateMixin, themableMixin],
  data () {
    return {
      editing: false,
      replying: false,
      content: null,
      replies: null,
      replyContent: null
    }
  },
  components: {
    CommentReply,
    CreateComment
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'isLoggedIn',
      currentUser: 'currentUser',
      isOwner: 'isOwner'
    }),
    commentOwner () {
      return this.isOwner(this.comment.attributes.user_id)
    },
    deletable () {
      return this.commentableOwner || this.commentOwner
    },
    reportable () {
      return !this.commentOwner
    },
    editable () {
      return this.commentOwner
    },
    themableTheme () {
      return this.theme ? this.theme.name : null
    }
  },
  methods: {
    confirmDeletion () {
      this.$buefy.dialog.confirm({
        message: 'Do you want to delete your comment?',
        onConfirm: () => this.$emit('deleteComment', this.comment.id)
      })
    },
    resizeComment () {
      if (this.$refs.comment) {
        var el = this.$refs.comment.$el.childNodes[0]
        setTimeout(function () {
          el.style.height = 'auto'
          el.style.height = el.scrollHeight + 'px'
          el.style.overflow += 'hidden'
        }, 0)
      }
    },
    cancelEditing () {
      this.editing = false
      this.content = this.comment.attributes.content
    },
    startEditing () {
      this.editing = true
      this.$nextTick(() => {
        this.$refs.comment.$el.childNodes[0].focus()
        this.resizeComment()
        this.$refs.comment.$el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        })
      })
    },
    saveEditing () {
      this.editing = false
      this.$emit('updateComment', { comment: this.comment.id, content: this.content })
    },
    startReplying (value) {
      if (value) this.replyContent = value
      this.replying = true
    },
    addReply (value) {
      this.$store
        .dispatch('comments/replies/create', { comment: this.comment.id, content: value })
        .then((resp) => {
          this.replies = resp.data.data
        })
        .then((_) => {})
      this.replying = false
    },
    deleteReply (value) {
      this.$store
        .dispatch('comments/replies/destroy', { comment: this.comment.id, reply: value })
        .then((resp) => {
          this.replies = resp.data.data
        })
        .then((_) => {})
    },
    updateReply ({ reply, content }) {
      this.$store
        .dispatch('comments/replies/update', {
          comment: this.comment.id,
          reply: reply,
          content: content
        })
        .then((resp) => {
          this.replies = resp.data.data
        })
        .then((_) => {})
    },
    cancelReply () {
      this.replying = false
    },
    fromNow (date) {
      return moment(date).fromNow()
    },
    fetchReplies () {
      this.$store
        .dispatch('comments/replies/index', this.comment.id)
        .then((resp) => {
          this.replies = resp.data.data
        })
        .then((_) => {})
    }
  },
  created () {
    this.content = this.comment.attributes.content
    this.fetchReplies()
  },
  watch: {
    content () {
      this.resizeComment()
    }
  }
}
</script>

<style scoped></style>
